import { useCallback } from "react";

import { formatMessage, type MessageValues } from "../../i18n/catalogUtils";
import type { InstallableLocale } from "../../i18n/preparedLocales";
import { useWorkspaceCopy } from "../../i18n/workspaceCatalog";

const en = {
  parserTitle: "Parser package signing",
  parserBody: "Create a retained signing identity for this installation. Private keys stay in protected server custody; operators never upload or download them.",
  parserCreate: "Create parser signing key",
  parserReload: "Reload signing identities",
  parserNone: "No parser signing identity has been created.",
  parserFingerprint: "Public key fingerprint",
  parserPreviousFingerprint: "Previous signing key",
  parserActivate: "Activate for parser signing",
  parserRotate: "Replace the active signer",
  parserRetire: "Stop new signing",
  parserRevoke: "Revoke verification trust",
  parserCreateBody: "Create a separate key for parser packages. The server retains it across restarts and includes its custody volume in the existing protected recovery path. Creation does not activate it.",
  parserActivateBody: "Activate this server-owned key as a parser publisher. Replacing an active signer stops new signatures with the previous key while keeping its older packages verifiable.",
  parserRetireBody: "Stop issuing new signatures with this key. Existing package signatures remain trusted until verification trust is explicitly revoked.",
  parserRevokeBody: "All parser approvals using this public key and their registered releases will become unavailable. Signing history and retained key material are preserved. A revoked key cannot be approved again.",
  parserName: "Publisher name",
  parserReason: "Reason for this change",
  parserConfirm: "I confirm this signing authority change for this installation.",
  parserCancel: "Cancel",
  parserWorking: "Working…",
  parserError: "The signing operation could not be completed. Check custody availability, permissions and recent MFA, then reload and review again.",
  parserHealthy: "Retained key available",
  parserUnavailable: "Retained key unavailable — restore custody before issuing signatures.",
  parserState_provisioning: "Creating key",
  parserState_staged: "Awaiting activation",
  parserState_abandoned: "Abandoned",
  parserState_active: "Active for new signatures",
  parserState_retired: "Verification only",
  parserState_revoked: "Trust revoked",
  parserPrepareTitle: "Sign with your installation’s publisher",
  parserPrepareBody: "These are unsigned build inputs. Review their source and limitations, then sign them with the retained parser key. Runtime validation is still required afterwards.",
  parserSetup: "Set up parser signing in Keys & Trust",
  parserChooseSigner: "Active server-owned publisher",
  parserNoSigner: "No healthy active parser signer is available. Create and activate one in Keys & Trust.",
  parserReviewInputs: "Review build inputs",
  parserSignInputs: "Sign and continue",
  parserConfirmInputs: "I reviewed the source, version and limitations and authorise this installation to sign these exact inputs.",
  parserSource: "Declared source commit",
  parserVersion: "Release version",
  parserLimitations: "Declared limitations",
  parserInputsHash: "Build inputs SHA-256",

  overview: "Overview",
  inventory: "Trust inventory",
  tufHealth: "Collector update security",
  eyebrow: "TRUST & KEY MANAGEMENT",
  title: "Keys & Trust",
  description: "Review release trust, manage Kavoosh-owned signing custody and inspect custody health without exposing private key material.",
  custodyTitle: "Private material stays in custody",
  custodyBody: "Only public identity and health metadata are shown.",
  sectionsAria: "Keys and Trust sections",
  navGroup: "Trust",
  familyCollectorRelease: "Collector release",
  familyCollectorDevice: "Collector & device",
  familyEvidenceCustody: "Evidence & custody",
  familyAnalysisTrust: "Analysis & reports",
  familyPlatformSecurity: "Platform security",
  familyIntegrations: "Integrations",
  healthHealthy: "Healthy",
  healthMissing: "Missing",
  healthUnavailable: "Unavailable",
  healthUnsafePermissions: "Unsafe permissions",
  healthInvalidPublicKey: "Invalid public key",
  healthNotConfigured: "Not configured",
  healthExternalCustody: "External custody",
  readinessReadyTitle: "Release trust ready",
  readinessReadyBody: "All release-required Kavoosh authorities visible to this control plane are healthy.",
  readinessBlockedTitle: "Release trust blocked",
  readinessBlockedBody: "One or more Kavoosh-managed release authorities need attention before release operations can proceed.",
  readinessReconcileTitle: "Release trust needs reconciliation",
  readinessReconcileBody: "External Authenticode or TUF custody is still outside the Kavoosh-managed trust registry.",
  errorAdminRequired: "Platform Admin authority is required to view Keys & Trust.",
  errorLoad: "Keys & Trust health could not be loaded.",
  errorContract: "Keys & Trust response failed its safety contract.",
  checkingProviders: "Checking trust providers…",
  inventoryEyebrow: "SEARCHABLE AUTHORITY REGISTRY",
  inventoryTitle: "Trust inventory",
  inventoryBody: "Find an authority by purpose, provider, consumer or health state instead of scanning every key record.",
  checking: "Checking…",
  refresh: "Refresh",
  filterAria: "Filter trust authorities",
  search: "Search",
  searchPlaceholder: "Authority, consumer, provider…",
  family: "Family",
  allFamilies: "All families",
  health: "Health",
  allStates: "All states",
  needsAttention: "Needs attention",
  resultOf: "of {count}",
  clearFilters: "Clear filters",
  columnAuthority: "Authority",
  columnState: "State",
  columnFamily: "Family",
  columnCustody: "Custody",
  columnRelease: "Release",
  columnUsedBy: "Used by",
  fingerprint: "Fingerprint {value}",
  required: "Required",
  no: "No",
  noMatches: "No authorities match these filters.",
  statsAria: "Collector release trust summary",
  trustInventoryStat: "Trust inventory",
  environment: "{name} environment",
  releaseAuthorities: "Release authorities",
  requiredByFlows: "Required by release flows",
  releaseBlockers: "Release blockers",
  managedRemediation: "Kavoosh-managed remediation",
  externalReleaseCustody: "External release custody",
  visibleNotManaged: "Visible, not Kavoosh-managed",
  readinessAria: "Collector release trust readiness",
  readinessEyebrow: "COLLECTOR RELEASE READINESS",
  priorityEyebrow: "RELEASE PRIORITY",
  priorityTitle: "What blocks release operations",
  noManagedRemediation: "No Kavoosh-managed release authority currently needs remediation.",
  scopeNote: "Other platform, evidence and integration authority findings remain available in Trust inventory and do not crowd this release-focused overview.",
  openInventory: "Open trust inventory",
  externalTitle: "{count} external release authorities are not yet reconciled",
  externalBody: "They remain visible for release readiness, but Kavoosh does not claim custody of their private material.",
  reviewExternal: "Review external custody",
  boundaryTitle: "Security boundary",
  boundaryBody: "Private key bytes never return through this interface. Managed actions operate on provider-owned identities, while this page exposes only public identity, lifecycle and health metadata.",
  stateReady: "Ready",
  stateBlocked: "Blocked",
  stateUnknown: "Unknown",
  loading: "Loading…",
  initializing: "Initializing…",
  working: "Working…",
  refreshing: "Refreshing…",
  version: "Version",
  remaining: "Remaining",
  expires: "Expires",
  pending: "Pending",
  provider: "Provider",
  keyId: "Key ID",
  fingerprintLabel: "Fingerprint",
  active: "Active",
  currentStep: "Current step",
  locked: "Locked",
  tufStatusHealthy: "Healthy",
  tufStatusExpiring: "Expiring",
  tufStatusExpired: "Expired",
  tufStatusInvalid: "Invalid",
  tufStatusUnavailable: "Unavailable",
  tufStatusUnconfigured: "Unconfigured",
  tufHealthLoadError: "TUF health could not be loaded.",
  tufBootstrapAlready: "Windows beta TUF is already reconciled at Root v{version}.",
  tufBootstrapRecovered: "Windows beta TUF public metadata recovery completed at Root v{version}.",
  tufBootstrapInitialized: "Windows beta TUF initialized at Root v{version}.",
  tufBootstrapError: "Initial Windows beta TUF bootstrap could not be completed.",
  tufMaintenanceAria: "Renew Windows beta update trust",
  tufMaintenanceTitle: "Renew public update trust",
  tufMaintenanceBody: "Kavoosh verifies the current Root, active managed signers and the current or historically valid metadata chain, keeps the exact current Collector release target unchanged, then publishes a fresh Targets, Snapshot and Timestamp generation. Renew early when metadata is expiring to avoid a release blocker.",
  tufMaintenanceAction: "Renew update trust",
  tufMaintenanceOpen: "Review & renew update trust",
  tufMaintenanceWorking: "Renewing…",
  tufMaintenanceBoundary: "Recent MFA is required. Timestamp is committed last. No release bytes, target digest or private key material are changed or exposed.",
  tufMaintenanceRenewed: "Public update trust renewed at metadata generation v{version}.",
  tufMaintenanceReconciled: "Public update trust recovery reconciled at metadata generation v{version}.",
  tufMaintenanceError: "Public update trust could not be renewed safely. Review the reported authority issue before trying again.",
  tufPolicyAria: "Windows beta update trust maintenance policy",
  tufPolicyTitle: "Update trust maintenance policy",
  tufPolicyBody: "Review or change the validity windows Kavoosh will use when it signs fresh TUF metadata. This is versioned Admin authority; values are not embedded in product source.",
  tufPolicyCreateTitle: "Create update trust maintenance policy",
  tufPolicyCreateBody: "No maintenance policy exists yet. Define the approved validity windows before Kavoosh can renew update trust.",
  tufPolicyNotConfigured: "No maintenance policy is configured yet",
  tufPolicyNotConfiguredBody: "Update trust renewal stays paused until you save an approved policy. Saving the policy does not renew or publish TUF metadata.",
  tufPolicyVersionPending: "A policy version is assigned only after the first successful save.",
  tufPolicyCreate: "Create policy",
  tufPolicyOpen: "Update trust policy",
  tufPolicyEntryHelp: "Inspect or change validity and warning windows without leaving this page.",
  tufPolicyEdit: "Edit policy",
  tufPolicySave: "Save policy",
  tufPolicyCancel: "Cancel",
  tufPolicyLoadError: "Update trust maintenance policy could not be loaded.",
  tufPolicySaveError: "Update trust maintenance policy could not be saved.",
  tufPolicyValidation: "Enter whole-day values so Root > Targets > Snapshot > Timestamp > warning, each between 1 and 3650.",
  tufPolicyRequiredBeforeRenewal: "Configure update trust maintenance policy here before renewal. No trust metadata was changed.",
  tufPolicySavedContinue: "Maintenance policy saved. You can now renew update trust.",
  tufPolicySaved: "Maintenance policy v{version} saved.",
  tufPolicyVersion: "Policy v{version}",
  tufPolicySummary: "Root {root}d · Targets {targets}d · Snapshot {snapshot}d · Timestamp {timestamp}d · warning {warning}d",
  tufPolicyRoot: "Root validity (days)",
  tufPolicyTargets: "Targets validity (days)",
  tufPolicySnapshot: "Snapshot validity (days)",
  tufPolicyTimestamp: "Timestamp validity (days)",
  tufPolicyWarning: "Renewal warning (days)",
  tufPolicyRootHelp: "Longest-lived trust anchor. Use the lifetime approved for platform trust.",
  tufPolicyTargetsHelp: "How long Collector release authorization metadata remains valid.",
  tufPolicySnapshotHelp: "How long the signed repository-state snapshot remains valid.",
  tufPolicyTimestampHelp: "Freshness window for update metadata. This must be shorter than Snapshot.",
  tufPolicyWarningHelp: "How early Kavoosh should warn before Timestamp expires.",
  tufPolicyRule: "Enter all five approved durations. Required order: Root > Targets > Snapshot > Timestamp > warning.",
  tufPolicyRuleSatisfied: "Validity order is correct. Saving creates a new versioned Admin policy.",
  tufPolicyNoGuess: "Kavoosh does not invent trust lifetimes. Enter only durations approved by your organization. If no policy is approved yet, leave this unsaved; renewal remains blocked and no trust metadata changes.",
  tufPublicUpdateTrustEyebrow: "COLLECTOR RELEASE & UPDATE SECURITY",
  tufHealthBody: "Verified public Root, Targets, Snapshot and Timestamp state bound to the persisted Platform Root authority.",
  tufReleaseBlocking: "RELEASE BLOCKING",
  tufReadOnlyHealth: "READ-ONLY HEALTH",
  tufBootstrapAria: "Initial Windows beta TUF bootstrap",
  tufBootstrapTitle: "Initial Windows beta trust bootstrap",
  tufBootstrapBody: "One-time high-impact action. Kavoosh requires exactly one staged P-256 Targets, Snapshot and Timestamp identity, creates a dedicated protected-local Root key, stages and verifies public metadata, then commits Timestamp last. A retry is accepted only for exact recovery.",
  tufBootstrapAction: "Initialize / recover Windows beta TUF",
  tufRecentMfaBoundary: "Recent MFA is required. Private key bytes are never returned to the browser.",
  tufHealthFooter: "TUF health reads only the public metadata mount. Routine mutable-role signing remains P-256 provider authority; the bootstrap control above is a separate one-time initial-trust ceremony.",
  tufLoadManagedError: "Managed TUF signing identities could not be loaded.",
  tufStageWrongPurpose: "Managed TUF staging returned the wrong signing purpose.",
  tufSignerStaged: "{role} signer staged in protected-local P-256 custody.",
  tufStageError: "Managed TUF signing identity could not be staged.",
  tufDiscardConfirm: "Discard newer duplicate {role} signer {key}? The oldest staged identity will be preserved.",
  tufDiscardWrongPurpose: "Managed TUF duplicate cleanup returned the wrong signing purpose.",
  tufDuplicateDiscarded: "{role} duplicate discarded; the oldest staged identity was preserved.",
  tufDiscardError: "Managed TUF duplicate cleanup could not be completed.",
  tufPreflightMismatch: "Managed TUF activation preflight does not match the staged identity or Windows beta scope.",
  tufPreflightError: "Managed TUF activation preflight could not be completed.",
  tufActivateConfirm: "Activate {role} for Windows beta under Root v{version}? This records Root-bound authority only. It does not publish TUF metadata.",
  tufActivationMismatch: "Managed TUF activation does not match the verified Root preflight.",
  tufActivatedMessage: "{role} is ACTIVE for Windows beta. Stop here and complete API restart verification before continuing.",
  tufActivationError: "Managed TUF activation could not be completed.",
  tufActivationEyebrow: "TUF ACTIVATION · WINDOWS BETA",
  tufActivationTitle: "Activate TUF signing roles",
  tufActivationBody: "One controlled path: Targets first, Snapshot second, Timestamp last. Only the current step can be verified or activated.",
  tufFixedCheckpointTitle: "Windows beta is fixed for this activation checkpoint",
  tufFixedCheckpointBody: "No channel selector is shown here to prevent accidental stable-channel activation.",
  tufActiveCount: "{count}/3 active",
  tufRestartTitle: "Stop here — restart verification required",
  tufRestartBody: "The next TUF role stays locked in this page until the API restart and immutable-state audit are completed. Reload this page only after that checkpoint passes.",
  tufActivationOrderAria: "TUF activation order",
  tufTargetsDescription: "Authorize the key that signs release target metadata.",
  tufSnapshotDescription: "Authorize the key that signs the repository snapshot.",
  tufTimestampDescription: "Authorize the final freshness key. This is always last.",
  tufSnapshotPrerequisite: "Available after Targets is activated and restart-verified.",
  tufTimestampPrerequisite: "Available after Targets and Snapshot are activated and restart-verified.",
  tufStateActive: "ACTIVE",
  tufStateRecoveryRequired: "RECOVERY REQUIRED",
  tufStateSignerMissing: "SIGNER MISSING",
  tufStateCurrent: "CURRENT",
  tufStateLocked: "LOCKED",
  tufKeySummary: "Key {key}",
  tufProtectedLocalCustody: "P-256 · protected local custody",
  tufActivatedUnderRoot: "Activated under Root v{version}",
  tufActivatedDetail: "Windows beta · threshold {threshold} · current Root/custody reconciliation passed.",
  tufNotAvailableYet: "Not available yet",
  tufRestartBeforeStep: "Restart and immutable-state verification must pass before this step opens.",
  tufResolveDuplicateTitle: "Resolve the duplicate before activation",
  tufResolveDuplicateBody: "The oldest staged identity is preserved. Discard only the newer duplicate.",
  tufDiscardDuplicateAria: "Discard newer duplicate {role} signer",
  tufDiscarding: "Discarding…",
  tufDiscardButton: "Discard {key}",
  tufNoStagedSigner: "No staged {role} signer",
  tufNoStagedSignerBody: "Use Signer setup & recovery below to create the missing P-256 signer.",
  tufVerifyAuthorityTitle: "Verify authority before activation",
  tufVerifyAuthorityBody: "Server-side verification checks this exact key against the current trusted Root and protected-local custody.",
  tufVerifyAuthorityAria: "Verify {role} authority",
  tufVerifying: "Verifying…",
  tufVerifyAuthorityButton: "Verify {role} authority",
  tufAuthorityVerified: "Authority verified",
  tufVerifiedDetail: "Root v{version} · threshold {threshold} · Windows beta · role {role}",
  tufActivateAria: "Activate {role}",
  tufActivating: "Activating…",
  tufActivateButton: "Activate {role}",
  tufTechnicalDetails: "Technical details",
  tufSignerSetup: "Signer setup & recovery",
  tufSignerSetupRole: "Signer setup role",
  tufStagedSignerExists: "Staged signer already exists",
  tufGenerateMissingSigner: "Generate missing TUF signer",
  tufSignerSetupBody: "Use this only to create a missing staged signer or recover duplicate staging. Activation stays in the ordered workflow above.",
  tufProviderNote: "Activation records Root-bound authority only. It does not mutate Root or publish Collector Release metadata. Collector packages are registered from the artifact-first Collector Releases workflow.",
} as const;

export type KeysTrustManagedMessageKey = keyof typeof en;
type PartialCatalog = Partial<Record<KeysTrustManagedMessageKey, string>>;
const fa: PartialCatalog = {
  parserTitle: "امضای بسته‌های پارسر",
  parserBody: "برای این نصب هویت امضای پایدار بسازید. کلید خصوصی در محل حفاظت‌شدهٔ سرور می‌ماند و ادمین آن را آپلود یا دانلود نمی‌کند.",
  parserCreate: "ساخت کلید امضای پارسر",
  parserReload: "بارگیری دوبارهٔ هویت‌های امضا",
  parserNone: "هنوز هویت امضای پارسر ساخته نشده است.",
  parserFingerprint: "اثر انگشت کلید عمومی",
  parserPreviousFingerprint: "کلید امضای قبلی",
  parserActivate: "فعال‌سازی برای امضای پارسر",
  parserRotate: "جایگزینی امضاکنندهٔ فعال",
  parserRetire: "توقف امضاهای جدید",
  parserRevoke: "لغو اعتماد به امضا",
  parserCreateBody: "کلید جداگانه‌ای برای بسته‌های پارسر ساخته می‌شود. سرور آن را پس از راه‌اندازی دوباره حفظ می‌کند و حجم نگه‌داری کلید در مسیر حفاظت‌شدهٔ بازیابی فعلی قرار دارد. ساخت کلید به معنی فعال‌سازی آن نیست.",
  parserActivateBody: "این کلید متعلق به سرور را به‌عنوان ناشر پارسر فعال کنید. جایگزینی کلید فعال، امضای جدید با کلید قبلی را متوقف می‌کند و اعتبار بسته‌های قبلی را نگه می‌دارد.",
  parserRetireBody: "امضای بسته‌های جدید با این کلید متوقف می‌شود. اعتبار امضاهای قبلی تا زمان لغو صریح اعتماد باقی می‌ماند.",
  parserRevokeBody: "همهٔ تأییدهای ناشر با این کلید عمومی و ریلیزهای وابسته از دسترس خارج می‌شوند. تاریخچهٔ امضا و کلید نگه‌داری‌شده حفظ می‌شوند. کلید لغوشده دوباره قابل تأیید نیست.",
  parserName: "نام ناشر",
  parserReason: "دلیل این تغییر",
  parserConfirm: "این تغییر اختیار امضا را برای این نصب تأیید می‌کنم.",
  parserCancel: "انصراف",
  parserWorking: "در حال انجام…",
  parserError: "عملیات امضا انجام نشد. دسترسی به کلید، مجوزها و احراز هویت چندعاملی اخیر را بررسی کنید؛ سپس وضعیت را دوباره دریافت و بازبینی کنید.",
  parserHealthy: "کلید نگه‌داری‌شده در دسترس است",
  parserUnavailable: "کلید نگه‌داری‌شده در دسترس نیست؛ پیش از امضای جدید محل نگه‌داری را بازیابی کنید.",
  parserState_provisioning: "در حال ساخت کلید",
  parserState_staged: "در انتظار فعال‌سازی",
  parserState_abandoned: "رهاشده",
  parserState_active: "فعال برای امضاهای جدید",
  parserState_retired: "فقط تأیید امضاهای قبلی",
  parserState_revoked: "اعتماد لغوشده",
  parserPrepareTitle: "امضا با ناشر همین نصب",
  parserPrepareBody: "این فایل، ورودی ساختِ امضانشده است. منبع و محدودیت‌ها را بازبینی و سپس با کلید پایدار پارسر امضا کنید. پس از امضا همچنان اعتبارسنجی اجرا لازم است.",
  parserSetup: "تنظیم امضای پارسر در Keys & Trust",
  parserChooseSigner: "ناشر فعال متعلق به سرور",
  parserNoSigner: "امضاکنندهٔ فعال و سالمی برای پارسر در دسترس نیست. در Keys & Trust یک کلید بسازید و فعال کنید.",
  parserReviewInputs: "بازبینی ورودی ساخت",
  parserSignInputs: "امضا و ادامه",
  parserConfirmInputs: "منبع، نسخه و محدودیت‌ها را بازبینی کردم و امضای همین ورودی‌ها با اختیار این نصب را مجاز می‌کنم.",
  parserSource: "کامیت منبع اعلام‌شده",
  parserVersion: "نسخهٔ ریلیز",
  parserLimitations: "محدودیت‌های اعلام‌شده",
  parserInputsHash: "SHA-256 ورودی ساخت",

  overview: "نمای کلی",
  inventory: "موجودی اعتماد",
  tufHealth: "امنیت به‌روزرسانی کالکتور",
  eyebrow: "مدیریت اعتماد و کلید",
  title: "کلیدها و اعتماد",
  description: "اعتماد انتشار، نگه‌داری امضاهای تحت مالکیت Kavoosh و سلامت custody را بدون افشای کلید خصوصی بازبینی کنید.",
  custodyTitle: "ماده خصوصی در custody باقی می‌ماند",
  custodyBody: "فقط هویت عمومی و متادیتای سلامت نمایش داده می‌شود.",
  sectionsAria: "بخش‌های کلیدها و اعتماد",
  navGroup: "اعتماد",
  familyCollectorRelease: "انتشار Collector",
  familyCollectorDevice: "Collector و دستگاه",
  familyEvidenceCustody: "Evidence و custody",
  familyAnalysisTrust: "تحلیل و گزارش‌ها",
  familyPlatformSecurity: "امنیت پلتفرم",
  familyIntegrations: "یکپارچه‌سازی‌ها",
  healthHealthy: "سالم",
  healthMissing: "موجود نیست",
  healthUnavailable: "در دسترس نیست",
  healthUnsafePermissions: "مجوزهای ناامن",
  healthInvalidPublicKey: "کلید عمومی نامعتبر",
  healthNotConfigured: "پیکربندی نشده",
  healthExternalCustody: "custody خارجی",
  readinessReadyTitle: "اعتماد انتشار آماده است",
  readinessReadyBody: "همه مراجع Kavoosh که برای انتشار الزامی‌اند و در این control plane دیده می‌شوند سالم هستند.",
  readinessBlockedTitle: "اعتماد انتشار مسدود است",
  readinessBlockedBody: "یک یا چند مرجع انتشار تحت مدیریت Kavoosh پیش از ادامه عملیات انتشار نیازمند رسیدگی است.",
  readinessReconcileTitle: "اعتماد انتشار نیازمند تطبیق است",
  readinessReconcileBody: "custody خارجی Authenticode یا TUF هنوز خارج از رجیستری اعتماد تحت مدیریت Kavoosh است.",
  errorAdminRequired: "برای مشاهده کلیدها و اعتماد، اختیار Platform Admin لازم است.",
  errorLoad: "وضعیت سلامت کلیدها و اعتماد قابل دریافت نیست.",
  errorContract: "پاسخ کلیدها و اعتماد قرارداد ایمنی خود را پاس نکرد.",
  checkingProviders: "در حال بررسی ارائه‌دهندگان اعتماد…",
  inventoryEyebrow: "رجیستری قابل جستجوی مراجع",
  inventoryTitle: "موجودی اعتماد",
  inventoryBody: "مرجع را بر اساس هدف، ارائه‌دهنده، مصرف‌کننده یا وضعیت سلامت پیدا کنید؛ نیازی به مرور تک‌تک رکوردهای کلید نیست.",
  checking: "در حال بررسی…",
  refresh: "تازه‌سازی",
  filterAria: "فیلتر مراجع اعتماد",
  search: "جستجو",
  searchPlaceholder: "مرجع، مصرف‌کننده، ارائه‌دهنده…",
  family: "خانواده",
  allFamilies: "همه خانواده‌ها",
  health: "سلامت",
  allStates: "همه وضعیت‌ها",
  needsAttention: "نیازمند توجه",
  resultOf: "از {count}",
  clearFilters: "پاک کردن فیلترها",
  columnAuthority: "مرجع",
  columnState: "وضعیت",
  columnFamily: "خانواده",
  columnCustody: "custody",
  columnRelease: "انتشار",
  columnUsedBy: "مصرف‌کنندگان",
  fingerprint: "اثر انگشت {value}",
  required: "الزامی",
  no: "خیر",
  noMatches: "هیچ مرجعی با این فیلترها مطابقت ندارد.",
  statsAria: "خلاصه اعتماد انتشار Collector",
  trustInventoryStat: "موجودی اعتماد",
  environment: "محیط {name}",
  releaseAuthorities: "مراجع انتشار",
  requiredByFlows: "موردنیاز جریان‌های انتشار",
  releaseBlockers: "مسدودکننده‌های انتشار",
  managedRemediation: "اصلاح تحت مدیریت Kavoosh",
  externalReleaseCustody: "custody خارجی انتشار",
  visibleNotManaged: "قابل مشاهده، خارج از مدیریت Kavoosh",
  readinessAria: "آمادگی اعتماد انتشار Collector",
  readinessEyebrow: "آمادگی انتشار Collector",
  priorityEyebrow: "اولویت انتشار",
  priorityTitle: "چه چیزی عملیات انتشار را مسدود می‌کند",
  noManagedRemediation: "در حال حاضر هیچ مرجع انتشار تحت مدیریت Kavoosh نیازمند اصلاح نیست.",
  scopeNote: "یافته‌های سایر مراجع پلتفرم، Evidence و یکپارچه‌سازی همچنان در موجودی اعتماد در دسترس‌اند و این نمای متمرکز بر انتشار را شلوغ نمی‌کنند.",
  openInventory: "باز کردن موجودی اعتماد",
  externalTitle: "{count} مرجع خارجی انتشار هنوز تطبیق داده نشده‌اند",
  externalBody: "برای آمادگی انتشار قابل مشاهده‌اند، اما Kavoosh مالکیت ماده خصوصی آن‌ها را ادعا نمی‌کند.",
  reviewExternal: "بازبینی custody خارجی",
  boundaryTitle: "مرز امنیتی",
  boundaryBody: "بایت‌های کلید خصوصی هرگز از این رابط برنمی‌گردند. عملیات مدیریت‌شده روی هویت‌های تحت مالکیت ارائه‌دهنده انجام می‌شود و این صفحه فقط هویت عمومی، چرخه عمر و متادیتای سلامت را نمایش می‌دهد.",
  stateReady: "آماده",
  stateBlocked: "مسدود",
  stateUnknown: "نامشخص",
  loading: "در حال بارگذاری…",
  initializing: "در حال راه‌اندازی…",
  working: "در حال انجام…",
  refreshing: "در حال تازه‌سازی…",
  version: "نسخه",
  remaining: "زمان باقی‌مانده",
  expires: "انقضا",
  pending: "در انتظار",
  provider: "ارائه‌دهنده",
  keyId: "شناسه کلید",
  fingerprintLabel: "اثر انگشت",
  active: "فعال",
  currentStep: "مرحله فعلی",
  locked: "قفل‌شده",
  tufStatusHealthy: "سالم",
  tufStatusExpiring: "نزدیک انقضا",
  tufStatusExpired: "منقضی‌شده",
  tufStatusInvalid: "نامعتبر",
  tufStatusUnavailable: "در دسترس نیست",
  tufStatusUnconfigured: "پیکربندی نشده",
  tufHealthLoadError: "وضعیت سلامت TUF قابل دریافت نیست.",
  tufBootstrapAlready: "TUF نسخه Windows beta از قبل در Root v{version} تطبیق داده شده است.",
  tufBootstrapRecovered: "بازیابی متادیتای عمومی TUF برای Windows beta در Root v{version} کامل شد.",
  tufBootstrapInitialized: "TUF نسخه Windows beta در Root v{version} راه‌اندازی شد.",
  tufBootstrapError: "راه‌اندازی اولیه TUF برای Windows beta کامل نشد.",
  tufMaintenanceAria: "تمدید اعتماد به‌روزرسانی Windows beta",
  tufMaintenanceTitle: "تمدید اعتماد به‌روزرسانی عمومی",
  tufMaintenanceBody: "Kavoosh ابتدا Root فعلی، امضاکننده‌های مدیریت‌شده فعال و زنجیره متادیتای فعلی یا معتبر پیش از انقضا را بررسی می‌کند؛ سپس بدون تغییر target دقیق Collector، نسل تازه Targets، Snapshot و Timestamp را منتشر می‌کند. وقتی متادیتا نزدیک انقضا است می‌توان زودتر تمدید کرد تا به blocker تبدیل نشود.",
  tufMaintenanceAction: "تمدید اعتماد به‌روزرسانی",
  tufMaintenanceOpen: "بررسی و تمدید اعتماد به‌روزرسانی",
  tufMaintenanceWorking: "در حال تمدید…",
  tufMaintenanceBoundary: "MFA اخیر الزامی است. Timestamp در آخر ثبت می‌شود. بایت‌های release، digest هدف و ماده کلید خصوصی تغییر نمی‌کنند و افشا نمی‌شوند.",
  tufMaintenanceRenewed: "اعتماد به‌روزرسانی عمومی در نسل متادیتای v{version} تمدید شد.",
  tufMaintenanceReconciled: "بازیابی اعتماد به‌روزرسانی عمومی در نسل متادیتای v{version} تطبیق داده شد.",
  tufMaintenanceError: "تمدید امن اعتماد به‌روزرسانی ممکن نشد. پیش از تلاش دوباره، مشکل مرجع گزارش‌شده را بررسی کنید.",
  tufPolicyAria: "سیاست نگهداری اعتماد به‌روزرسانی Windows beta",
  tufPolicyTitle: "سیاست نگهداری اعتماد به‌روزرسانی",
  tufPolicyBody: "بازه‌های اعتبار متادیتای تازه TUF را بازبینی یا تغییر دهید. این تنظیم یک مرجع نسخه‌دار تحت مدیریت ادمین است و در کد محصول ثابت نشده است.",
  tufPolicyCreateTitle: "ایجاد سیاست نگهداری اعتماد به‌روزرسانی",
  tufPolicyCreateBody: "هنوز هیچ سیاست نگهداری وجود ندارد. پیش از تمدید اعتماد به‌روزرسانی، بازه‌های اعتبار تأییدشده را تعیین کنید.",
  tufPolicyNotConfigured: "هنوز سیاست نگهداری پیکربندی نشده است",
  tufPolicyNotConfiguredBody: "تمدید اعتماد به‌روزرسانی تا ذخیره یک سیاست تأییدشده متوقف می‌ماند. ذخیره سیاست به‌تنهایی هیچ متادیتای TUF را تمدید یا منتشر نمی‌کند.",
  tufPolicyVersionPending: "نسخه سیاست فقط پس از نخستین ذخیره موفق اختصاص داده می‌شود.",
  tufPolicyCreate: "ایجاد سیاست",
  tufPolicyOpen: "تنظیم سیاست اعتماد به‌روزرسانی",
  tufPolicyEntryHelp: "بازه‌های اعتبار و هشدار را بدون خروج از این صفحه بررسی یا تغییر دهید.",
  tufPolicyEdit: "ویرایش سیاست",
  tufPolicySave: "ذخیره سیاست",
  tufPolicyCancel: "لغو",
  tufPolicyLoadError: "سیاست نگهداری اعتماد به‌روزرسانی قابل دریافت نیست.",
  tufPolicySaveError: "سیاست نگهداری اعتماد به‌روزرسانی ذخیره نشد.",
  tufPolicyValidation: "مقادیر روز را به‌صورت عدد صحیح وارد کنید؛ ترتیب باید Root > Targets > Snapshot > Timestamp > هشدار باشد و هر مقدار بین ۱ تا ۳۶۵۰ باشد.",
  tufPolicyRequiredBeforeRenewal: "پیش از تمدید، سیاست نگهداری اعتماد به‌روزرسانی را همین‌جا تنظیم کنید. هیچ متادیتای اعتماد تغییر نکرد.",
  tufPolicySavedContinue: "سیاست نگهداری ذخیره شد. اکنون می‌توانید اعتماد به‌روزرسانی را تمدید کنید.",
  tufPolicySaved: "سیاست نگهداری نسخه {version} ذخیره شد.",
  tufPolicyVersion: "سیاست نسخه {version}",
  tufPolicySummary: "Root {root} روز · Targets {targets} روز · Snapshot {snapshot} روز · Timestamp {timestamp} روز · هشدار {warning} روز",
  tufPolicyRoot: "اعتبار Root (روز)",
  tufPolicyTargets: "اعتبار Targets (روز)",
  tufPolicySnapshot: "اعتبار Snapshot (روز)",
  tufPolicyTimestamp: "اعتبار Timestamp (روز)",
  tufPolicyWarning: "هشدار تمدید (روز)",
  tufPolicyRootHelp: "طولانی‌ترین مرجع اعتماد است. مدت تأییدشده برای اعتماد پلتفرم را وارد کنید.",
  tufPolicyTargetsHelp: "مدتی که متادیتای مجوز انتشار Collector معتبر می‌ماند.",
  tufPolicySnapshotHelp: "مدتی که snapshot امضاشده وضعیت مخزن معتبر می‌ماند.",
  tufPolicyTimestampHelp: "بازه تازگی متادیتای به‌روزرسانی است و باید از Snapshot کوتاه‌تر باشد.",
  tufPolicyWarningHelp: "چند روز پیش از انقضای Timestamp، Kavoosh هشدار تمدید را آغاز کند.",
  tufPolicyRule: "هر پنج مدت تأییدشده را وارد کنید. ترتیب الزامی: Root > Targets > Snapshot > Timestamp > هشدار.",
  tufPolicyRuleSatisfied: "ترتیب اعتبار درست است. ذخیره، یک نسخه جدید از سیاست ادمین ایجاد می‌کند.",
  tufPolicyNoGuess: "Kavoosh مدت‌های اعتماد را حدس نمی‌زند. فقط مقادیر تأییدشده سازمان را وارد کنید. اگر هنوز سیاستی تأیید نشده، فرم را ذخیره نکنید؛ تمدید مسدود می‌ماند و هیچ متادیتای اعتماد تغییر نمی‌کند.",
  tufPublicUpdateTrustEyebrow: "امنیت انتشار و به‌روزرسانی کالکتور",
  tufHealthBody: "وضعیت تأییدشده عمومی Root، Targets، Snapshot و Timestamp که به مرجع پایدار Platform Root متصل است.",
  tufReleaseBlocking: "مسدودکننده انتشار",
  tufReadOnlyHealth: "سلامت فقط‌خواندنی",
  tufBootstrapAria: "راه‌اندازی اولیه TUF برای Windows beta",
  tufBootstrapTitle: "راه‌اندازی اولیه اعتماد Windows beta",
  tufBootstrapBody: "یک اقدام یک‌باره و پراثر. Kavoosh دقیقاً یک هویت staged از نوع P-256 برای Targets، Snapshot و Timestamp می‌خواهد، یک کلید Root مجزای protected-local می‌سازد، متادیتای عمومی را staged و تأیید می‌کند و در پایان Timestamp را ثبت می‌کند. تلاش مجدد فقط برای بازیابی دقیق پذیرفته می‌شود.",
  tufBootstrapAction: "راه‌اندازی / بازیابی TUF برای Windows beta",
  tufRecentMfaBoundary: "MFA اخیر الزامی است. بایت‌های کلید خصوصی هرگز به مرورگر بازگردانده نمی‌شوند.",
  tufHealthFooter: "سلامت TUF فقط mount متادیتای عمومی را می‌خواند. امضای معمول نقش‌های قابل‌تغییر همچنان در اختیار ارائه‌دهنده P-256 است؛ کنترل bootstrap بالا یک مراسم یک‌باره اعتماد اولیه است.",
  tufLoadManagedError: "هویت‌های امضای مدیریت‌شده TUF قابل دریافت نیستند.",
  tufStageWrongPurpose: "staging مدیریت‌شده TUF هدف امضای نادرستی برگرداند.",
  tufSignerStaged: "امضاکننده {role} در custody محلی محافظت‌شده P-256 staged شد.",
  tufStageError: "هویت امضای مدیریت‌شده TUF قابل staged شدن نیست.",
  tufDiscardConfirm: "امضاکننده تکراری جدید {role} با کلید {key} حذف شود؟ قدیمی‌ترین هویت staged حفظ خواهد شد.",
  tufDiscardWrongPurpose: "پاک‌سازی تکراری TUF هدف امضای نادرستی برگرداند.",
  tufDuplicateDiscarded: "نسخه تکراری {role} حذف شد و قدیمی‌ترین هویت staged حفظ شد.",
  tufDiscardError: "پاک‌سازی هویت تکراری TUF کامل نشد.",
  tufPreflightMismatch: "preflight فعال‌سازی TUF با هویت staged یا دامنه Windows beta تطابق ندارد.",
  tufPreflightError: "preflight فعال‌سازی TUF کامل نشد.",
  tufActivateConfirm: "{role} برای Windows beta زیر Root v{version} فعال شود؟ این کار فقط مرجع متصل به Root را ثبت می‌کند و متادیتای TUF را منتشر نمی‌کند.",
  tufActivationMismatch: "فعال‌سازی TUF با preflight تأییدشده Root مطابقت ندارد.",
  tufActivatedMessage: "{role} برای Windows beta فعال شد. اینجا متوقف شوید و پیش از ادامه، تأیید restart API را کامل کنید.",
  tufActivationError: "فعال‌سازی مدیریت‌شده TUF کامل نشد.",
  tufActivationEyebrow: "فعال‌سازی TUF · WINDOWS BETA",
  tufActivationTitle: "فعال‌سازی نقش‌های امضای TUF",
  tufActivationBody: "یک مسیر کنترل‌شده: ابتدا Targets، سپس Snapshot و در پایان Timestamp. فقط مرحله فعلی قابل تأیید یا فعال‌سازی است.",
  tufFixedCheckpointTitle: "Windows beta برای این checkpoint فعال‌سازی ثابت است",
  tufFixedCheckpointBody: "برای جلوگیری از فعال‌سازی تصادفی کانال stable، در اینجا انتخاب‌گر کانال نمایش داده نمی‌شود.",
  tufActiveCount: "{count}/3 فعال",
  tufRestartTitle: "اینجا متوقف شوید — تأیید restart لازم است",
  tufRestartBody: "نقش بعدی TUF تا زمان تکمیل restart API و audit وضعیت تغییرناپذیر در این صفحه قفل می‌ماند. فقط بعد از پاس‌شدن این checkpoint صفحه را دوباره بارگذاری کنید.",
  tufActivationOrderAria: "ترتیب فعال‌سازی TUF",
  tufTargetsDescription: "کلیدی را مجاز می‌کند که متادیتای target انتشار را امضا می‌کند.",
  tufSnapshotDescription: "کلیدی را مجاز می‌کند که snapshot مخزن را امضا می‌کند.",
  tufTimestampDescription: "کلید نهایی freshness را مجاز می‌کند. این مرحله همیشه آخر است.",
  tufSnapshotPrerequisite: "پس از فعال‌شدن Targets و تأیید restart در دسترس است.",
  tufTimestampPrerequisite: "پس از فعال‌شدن Targets و Snapshot و تأیید restart در دسترس است.",
  tufStateActive: "فعال",
  tufStateRecoveryRequired: "نیازمند بازیابی",
  tufStateSignerMissing: "امضاکننده موجود نیست",
  tufStateCurrent: "فعلی",
  tufStateLocked: "قفل‌شده",
  tufKeySummary: "کلید {key}",
  tufProtectedLocalCustody: "P-256 · custody محلی محافظت‌شده",
  tufActivatedUnderRoot: "زیر Root v{version} فعال شد",
  tufActivatedDetail: "Windows beta · آستانه {threshold} · تطبیق فعلی Root/custody پاس شده است.",
  tufNotAvailableYet: "هنوز در دسترس نیست",
  tufRestartBeforeStep: "پیش از بازشدن این مرحله، restart و تأیید وضعیت تغییرناپذیر باید پاس شوند.",
  tufResolveDuplicateTitle: "پیش از فعال‌سازی نسخه تکراری را رفع کنید",
  tufResolveDuplicateBody: "قدیمی‌ترین هویت staged حفظ می‌شود. فقط نسخه تکراری جدیدتر را حذف کنید.",
  tufDiscardDuplicateAria: "حذف امضاکننده تکراری جدید {role}",
  tufDiscarding: "در حال حذف…",
  tufDiscardButton: "حذف {key}",
  tufNoStagedSigner: "هیچ امضاکننده staged برای {role} وجود ندارد",
  tufNoStagedSignerBody: "برای ساخت امضاکننده P-256 گمشده، از بخش راه‌اندازی و بازیابی امضاکننده در پایین استفاده کنید.",
  tufVerifyAuthorityTitle: "پیش از فعال‌سازی مرجع را تأیید کنید",
  tufVerifyAuthorityBody: "تأیید سمت سرور همین کلید دقیق را با Root مورد اعتماد فعلی و custody محلی محافظت‌شده بررسی می‌کند.",
  tufVerifyAuthorityAria: "تأیید مرجع {role}",
  tufVerifying: "در حال تأیید…",
  tufVerifyAuthorityButton: "تأیید مرجع {role}",
  tufAuthorityVerified: "مرجع تأیید شد",
  tufVerifiedDetail: "Root v{version} · آستانه {threshold} · Windows beta · نقش {role}",
  tufActivateAria: "فعال‌سازی {role}",
  tufActivating: "در حال فعال‌سازی…",
  tufActivateButton: "فعال‌سازی {role}",
  tufTechnicalDetails: "جزئیات فنی",
  tufSignerSetup: "راه‌اندازی و بازیابی امضاکننده",
  tufSignerSetupRole: "نقش امضاکننده",
  tufStagedSignerExists: "امضاکننده staged از قبل وجود دارد",
  tufGenerateMissingSigner: "ساخت امضاکننده TUF گمشده",
  tufSignerSetupBody: "این بخش را فقط برای ساخت امضاکننده staged گمشده یا بازیابی staging تکراری استفاده کنید. فعال‌سازی در همان روند مرتب‌شده بالا باقی می‌ماند.",
  tufProviderNote: "رکورد فعال‌سازی فقط مرجع متصل به Root را ثبت می‌کند. این کار Root را تغییر نمی‌دهد یا Collector Release را منتشر نمی‌کند. بسته‌های Collector از مسیر artifact-first در Collector Releases ثبت می‌شوند.",
};
const empty: PartialCatalog = {};
const catalogs: Record<InstallableLocale, PartialCatalog> = {
  en,
  fa,
  ar: empty,
  it: empty,
  ru: empty,
  es: empty,
  fr: empty,
  de: empty,
  tr: empty,
  uk: empty,
  "zh-cn": empty,
  sv: empty,
};

function message(locale: InstallableLocale, key: KeysTrustManagedMessageKey, values?: MessageValues): string {
  return formatMessage(catalogs[locale][key] ?? en[key], values);
}

export function useKeysTrustManagedCopy() {
  const { locale } = useWorkspaceCopy();
  const t = useCallback(
    (key: KeysTrustManagedMessageKey, values?: MessageValues) => message(locale, key, values),
    [locale],
  );
  return { locale, t };
}
